import "./ChartWidgetComponent.scss";
import template from "./ChartWidgetComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {CurrentUserService} from "../../../../../services/user/CurrentUserService";
import {FeatureNames} from "../../../../../types/UserConfig";
import {ChartApi} from "../../../../../components/chartiq/components/chart/ChartApi";
import {ChartWidgetConfig} from "./models/ChartWidgetConfig";
import {ComponentCallback} from "../../../../../types/AngularJS";
import {isNotNull} from "../../../../../js/util/UtilFunctions";
import {CodeAndExchange, securityEquality} from "app/instruments/types/CodeAndExchange";
import {ObjectUtil} from "../../../../../js/util/ObjectUtil";
import {CompanyInfo} from "../../../../../types/Company";
import {ChartConfigChange, HudChange, PeriodChange, SecurityChange, updateConfig} from "./models/ChartConfigChange";
import {ChartIQPeriodicity, chartIqPeriodicityEquality} from "../../../../../components/chartiq/types/ChartIQ";
import {WidgetDisplayMode} from "../../../models/WidgetDisplayMode";

export const ChartWidget = "tfChartWidget";

export function chartWidget(): IComponentOptions {
    return {
        template,
        bindings: {
            security: "<",
            config: "<",
            displayMode: "<",
            onSecurityChanged: "&",
            onConfigChanged: "&"
        },
        controllerAs: "ctrl",
        controller: ChartWidgetComponentController
    };
}

export class ChartWidgetComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            this.security$.next(security);
        }
    }

    private readonly security$ = new ReplaySubject<CodeAndExchange>(1);

    /** @input */
    public set displayMode(displayMode: WidgetDisplayMode | null) {
        if (displayMode != null) {
            this.displayMode$.next(displayMode);
        }
    }

    private readonly displayMode$ = new ReplaySubject<WidgetDisplayMode>(1);

    /** @input */
    public set config(config: ChartWidgetConfig | null) {
        if (config != null) {
            this.config$.next(config);
        }
    }

    private readonly config$ = new BehaviorSubject<ChartWidgetConfig | null>(null);

    /** @output */
    public readonly onSecurityChanged: ComponentCallback;

    /** @output */
    public readonly onConfigChanged: ComponentCallback;

    public readonly hasFullCharting$ = new BehaviorSubject(false);
    public readonly currentSecurity$ = new ReplaySubject<string>(1);

    private readonly chartApi$ = new ReplaySubject<ChartApi>(1);
    private readonly configUpdates$ = new ReplaySubject<ChartConfigChange>(1);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService) {
    }

    public $onInit(): void {
        this.watchConfigChanges();
        this.watchSecurityInputChanged();
        this.watchCurrentPeriod();
        this.watchConfig();
        this.watchHasFullCharting();
        this.watchShowHud();
    }

    private watchConfigChanges() {
        const config$ = this.config$.startWith(null);
        this.configUpdates$
            .takeUntil(this.destroyed$)
            .withLatestFrom(config$)
            .map(([change, config]) => updateConfig(change, config))
            .subscribe((config) => this.onConfigChanged({config}));
    }

    private watchSecurityInputChanged() {
        this.security$
            .takeUntil(this.destroyed$)
            .distinctUntilChanged(securityEquality())
            .map((security) => SecurityChange(security))
            .subscribe((change) => this.configUpdates$.next(change));
    }

    private watchCurrentPeriod() {
        const currentPeriod$ = this.config$
                                   .filter(isNotNull)
                                   .map((c) => c.period)
                                   .filter(isNotNull)
                                   .distinctUntilChanged(chartIqPeriodicityEquality());

        Observable.combineLatest(this.chartApi$, currentPeriod$)
                  .takeUntil(this.destroyed$)
                  .subscribe(([chartApi, currentPeriod]) => chartApi.setPeriod(currentPeriod));
    }

    private watchConfig() {
        this.config$
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .map((c) => c.selected)
            .filter(isNotNull)
            .map((s) => s.code)
            .distinctUntilChanged()
            .subscribe((security) => this.currentSecurity$.next(security));
    }

    private watchHasFullCharting() {
        this.currentUserService
            .current
            .takeUntil(this.destroyed$)
            .map((user) => user.hasFeature(FeatureNames.FEATURE_ADV_CHARTING))
            .subscribe((hasFullCharting) => this.hasFullCharting$.next(hasFullCharting));
    }

    public codeChanged(company: CompanyInfo) {
        const security = ObjectUtil.pick(company, "code", "exchange");
        const change = SecurityChange(security);
        this.configUpdates$.next(change);
        this.onSecurityChanged({security});
    }

    public chartCreated(api: ChartApi) {
        this.chartApi$.next(api);
    }

    private watchShowHud() {
        const showHud$ = this.config$
                             .filter(isNotNull)
                             .map((c) => c.showHud)
                             .filter(isNotNull)
                             .startWith(false);
        Observable.combineLatest(this.chartApi$, showHud$)
                  .takeUntil(this.destroyed$)
                  .subscribe(([chartApi, showHud]) => {
                      chartApi.hudVisible(showHud);
                      if (showHud) {
                          chartApi.enabledCrosshairSnapping();
                      } else {
                          chartApi.disableCrosshairSnapping();
                      }
                  });
    }

    public toggleHud() {
        const change = HudChange();
        this.configUpdates$.next(change);
    }

    public periodChanged(period: ChartIQPeriodicity) {
        const change = PeriodChange(period);
        this.configUpdates$.next(change);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
